import { Skeleton, SxProps, TextField } from '@mui/material';
import { forwardRef } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

type AppTextFieldProps<T extends FieldValues> = {
  name: Path<T>;
  type?: string;
  control: Control<any, any>;
  label?: string;
  id: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  sx?: SxProps;
  slotProps?: object;
  showSkeleton?: boolean;
  row?: number;
  multiline?: boolean;
};

const AppTextField = forwardRef(function ControlledTextField<
  T extends FieldValues
>(
  {
    name,
    type = 'text',
    control,
    label,
    id,
    disabled = false,
    required = false,
    fullWidth = false,
    sx,
    slotProps = {},
    showSkeleton = false,
    row,
    multiline = false,
  }: AppTextFieldProps<T>,
  ref: React.Ref<HTMLInputElement>
) {
  if (showSkeleton) {
    return (
      <Skeleton
        variant="rounded"
        height={40}
        sx={{
          width: 200,
          borderRadius: '12px',
          ...sx,
        }}
      />
    );
  }

  const isPassword =
    type === 'password' ||
    String(name).toLowerCase().includes('password');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        if (!isPassword) {
          return (
            <TextField
              {...field}
              type={type}
              ref={ref}
              id={id}
              label={label}
              margin="dense"
              size="medium"
              variant="outlined"
              required={required}
              disabled={disabled}
              error={!!fieldState?.error}
              autoComplete="off"
              helperText={fieldState?.error?.message}
              fullWidth={fullWidth}
              value={field.value ?? ''}            
              slotProps={slotProps}
              multiline={multiline}
              rows={row}
              sx={sx}
            />
          );
        }
        const { value, ref: fieldRef, ...restField } = field;

        return (
          <TextField
            {...restField}                         
            type={type}                             
            ref={ref ?? (fieldRef as any)}
            id={id}
            label={label}
            margin="dense"
            size="medium"
            variant="outlined"
            required={required}
            disabled={disabled}
            error={!!fieldState?.error}
            defaultValue=""                       
            autoComplete="current-password"
            helperText={fieldState?.error?.message}
            fullWidth={fullWidth}
            slotProps={slotProps}
            multiline={multiline}
            rows={row}
            sx={sx}
          />
        );
      }}
    />
  );
});

export default AppTextField;
